import { useMemo } from 'react'

import { formatMoney } from 'accounting'
import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOut } from '@fortawesome/pro-light-svg-icons'
import { faStar } from '@fortawesome/pro-solid-svg-icons'

import {
  Link, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import { digObject, generateRedirectUrl } from '@domain-group/javascript-utils'

import { generateUrls } from '@hooks/useCampaignQuote'
import useSelector from '@hooks/useSelector'

import { statusColors } from '@functions/status'

import type { CampaignQuoteModel, CampaignQuoteStatusKeys } from '@models/types'

import type { EntitiesState } from '@redux/entities'

const buildColumns = (
  campaigns: EntitiesState['campaigns'],
  currentStatus: 'all' | CampaignQuoteStatusKeys,
  statuses: EntitiesState['statuses'],
  selectedPriorityUserId?: number,
) => [
  {
    title: '',
    dataKey: 'priority_campaign_user_ids',
    cellProps: {
      width: 12,
      verticalAlign: 'middle',
    },
    render: (priority_campaign_user_ids: number[]) => (
      (selectedPriorityUserId && priority_campaign_user_ids?.includes(selectedPriorityUserId)) && (
        <Text color="blue">
          <FontAwesomeIcon icon={faStar} />
        </Text>
      )
    ),
  },
  {
    title: 'Quote Title',
    dataKey: 'title',
    cellProps: {
      verticalAlign: 'middle',
    },
    render: (title: string, campaignQuote: CampaignQuoteModel) => {
      const { campaign_id, id } = campaignQuote

      const { editCampaignQuoteUrl } = generateUrls(
        { id },
        { prefixRoute: `campaigns/${campaign_id}/edit` },
      )

      return (
        <Link
          href={`${editCampaignQuoteUrl}?redirect=${generateRedirectUrl()}`}
          textProps={{
            color: 'bodyFontColor',
            display: 'inline-block',
            variant: 'ellipsis',
          }}
        >
          {title}
        </Link>
      )
    },
  },
  {
    title: 'Campaign',
    dataKey: 'campaign_id',
    render: (campaign_id: number) => {
      const campaign = campaigns[campaign_id] || {}
      return (
        <Text variant="ellipsis">
          {campaign?.title}
        </Text>
      )
    },
  },
  {
    title: 'Price',
    dataKey: 'total',
    cellProps: {
      textAlign: 'right',
    },
    render: (total: number) => formatMoney(total),
  },
  {
    title: 'Date',
    dataKey: 'dates',
    cellProps: {
      textAlign: 'center',
      width: 120,
    },
    render: (dates: object) => {
      const createdDate = digObject(dates, 'created.date_time')
      const formattedDate = createdDate
        ? DateTime.fromFormat(createdDate, 'dd/MM/yyyy hh:mm').toFormat('d LLL yyyy')
        : 'N/A'

      return (
        <StatusBadge
          boxProps={{ width: 90 }}
          text={formattedDate}
          color="lightestGrey"
          textProps={{ color: 'bodyFontLightColor' }}
        />
      )
    },
  },
  {
    title: 'Status',
    dataKey: 'status_id',
    cellProps: {
      textAlign: 'center',
    },
    render: (status_id: number, campaignQuote: CampaignQuoteModel) => {
      const status = statuses[status_id] || {}
      const statusBadgeWidth = currentStatus === 'all' ? 120 : 'auto'
      const { void: isVoid } = campaignQuote

      return (
        status?.key && (
          <StatusBadge
            boxProps={{ width: statusBadgeWidth }}
            // @ts-expect-error TS7053
            color={isVoid ? 'red' : statusColors[status.key]}
            ghost
            text={isVoid ? 'Void' : status.title}
          />
        )
      )
    },
  },
  {
    title: '',
    dataKey: 'id',
    cellProps: {
      width: 15,
    },
    render: (id: number, campaignQuote: CampaignQuoteModel) => {
      const { campaign_id } = campaignQuote

      const { editCampaignQuoteUrl } = generateUrls(
        { id },
        { prefixRoute: `campaigns/${campaign_id}/edit` },
      )

      return (
        <Link greyLink href={`${editCampaignQuoteUrl}?redirect=${generateRedirectUrl()}`}>
          <FontAwesomeIcon icon={faSignOut} />
        </Link>
      )
    },
  },
]

interface QuotesTableProps {
  currentStatus: 'all' | CampaignQuoteStatusKeys,
  filteredCampaignQuotes: CampaignQuoteModel[],
  selectedPriorityUserId?: number,
}

const QuotesTable = (props: QuotesTableProps) => {
  const { currentStatus, filteredCampaignQuotes, selectedPriorityUserId } = props

  const {
    campaigns,
    statuses,
    _updates: {
      // @ts-expect-error TS2339
      campaigns: campaignsUpdatedAt,
      // @ts-expect-error TS2339
      campaignQuotes: campaignQuotesUpdatedAt,
    },
  } = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(
    () => buildColumns(campaigns, currentStatus, statuses, selectedPriorityUserId),
    [filteredCampaignQuotes.length, campaignsUpdatedAt, campaignQuotesUpdatedAt, selectedPriorityUserId],
  )

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      data={filteredCampaignQuotes}
      stickyHeader
    />
  )
}

export default QuotesTable
